// src/modules/app-views/leads/pages/Init/modals/LeadsParametersModal/modals/AddDataSourceModal/Step2.jsx
// *******************************************************************************
// Step 2: Data Mapping — DE-PARA field templates
// *******************************************************************************
import { Form, Input } from 'antd';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import translateX from 'utils/translateX';

// Shared style for monospace template fields
const monoStyle = { fontFamily: 'monospace' };

// *******************************************************************************
// STEP CONTENT
// *******************************************************************************
const Step2Content = () => {
  const { form } = useSimpleFormContext();

  // Watch phone/email to dynamically toggle required asterisk on each other
  const phoneValue = Form.useWatch(['fieldMapping', 'phone'], form);
  const emailValue = Form.useWatch(['fieldMapping', 'email'], form);

  const phoneRequired = !(emailValue && emailValue.trim());
  const emailRequired = !(phoneValue && phoneValue.trim());

  return (
    <>
      {/* UniqueKey mapping — with fallback chain tooltip */}
      <SimpleInput
        name={['fieldMapping', 'uniqueKey']}
        label="unique_key"
        tooltip={translateX('unique_key_mapping_tooltip')}
        required
        maxLength={200}
        style={monoStyle}
        placeholder="{uniqueKey}"
      />

      {/* Name mapping */}
      <SimpleInput
        name={['fieldMapping', 'name']}
        label="name"
        required
        maxLength={200}
        style={monoStyle}
        placeholder="{name}"
      />

      {/* Phone mapping — required when email is empty */}
      <Form.Item
        name={['fieldMapping', 'phone']}
        label={translateX('phone')}
        required={phoneRequired}
        dependencies={[['fieldMapping', 'email']]}
        rules={[
          {
            validator: (_, value) => {
              const emailVal = form?.getFieldValue(['fieldMapping', 'email']);
              if ((value && value.trim()) || (emailVal && emailVal.trim())) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(translateX('phone_or_email_required')));
            },
          },
        ]}
      >
        <Input maxLength={200} style={monoStyle} placeholder="{phone}" />
      </Form.Item>

      {/* Email mapping — required when phone is empty */}
      <Form.Item
        name={['fieldMapping', 'email']}
        label={translateX('email')}
        required={emailRequired}
        dependencies={[['fieldMapping', 'phone']]}
        rules={[
          {
            validator: (_, value) => {
              const phoneVal = form?.getFieldValue(['fieldMapping', 'phone']);
              if ((value && value.trim()) || (phoneVal && phoneVal.trim())) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(translateX('phone_or_email_required')));
            },
          },
        ]}
      >
        <Input maxLength={200} style={monoStyle} placeholder="{email}" />
      </Form.Item>

      {/* Interest template */}
      <SimpleInput
        name="interestTemplate"
        label="interest_template"
        tooltip={translateX('interest_template_tooltip')}
        required
        maxLength={300}
        placeholder="interest_template_placeholder"
        style={monoStyle}
      />
    </>
  );
};

// *******************************************************************************
// STEP BUILDER
// *******************************************************************************
const step2 = () => ({
  stepTitle: 'data_mapping',
  content: () => <Step2Content />,
  validationFields: [
    ['fieldMapping', 'uniqueKey'],
    ['fieldMapping', 'name'],
    ['fieldMapping', 'phone'],
    ['fieldMapping', 'email'],
    'interestTemplate',
  ],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step2;
